import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import {
  createTemplateFormControlConstant,
  ScreenLayout,
} from '../../../modules/templates/constants/create-template.constant';
import { OrgDetailsService } from 'src/app/shared/services/org-details.service';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  IDepartmentsResponse,
  IDivisionsResponse,
  IOrganizationsResponse,
  IOrgData,
  IOrgDetails,
  IVerticalsResponse,
} from 'src/app/shared/interfaces/org-details.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { MODULE_NAME, TemplateStatus } from 'src/app/core/constants/common';

@Component({
  selector: 'app-organization-details',
  templateUrl: './organization-details.component.html',
  styleUrls: ['./organization-details.component.scss'],
})
export class OrganizationDetailsComponent implements OnInit {
  @Input() inputForm: FormGroup;
  @Input() viewMode: boolean;
  @Input() editMode: boolean;
  @Input() copyMode: boolean;
  @Input() data: IOrgData;
  @Input() layout: string;
  @Input() parentComponent: string;

  formControls = createTemplateFormControlConstant;
  orgDetails: IOrgDetails = {
    organizations: [],
    verticals: [],
    divisions: [],
    departments: [],
  };
  selectedOrganization: string;
  selectedVertical: string;
  selectedDivision: string;
  selectedDepartment: string;
  ScreenlayoutConstant = ScreenLayout;

  constructor(
    private readonly orgDetailsService: OrgDetailsService,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.getOrganizations();
  }

  get form(): { [key: string]: AbstractControl } {
    return this.inputForm?.controls;
  }

  getOrganizations(): void {
    this.orgDetailsService.getOrganizationsList().subscribe({
      next: (orgResponse: IOrganizationsResponse) => {
        if (!orgResponse.error && orgResponse.data?.length) {
          this.orgDetails.organizations = orgResponse.data.map((org) => {
            return {
              key: org,
              value: org,
            };
          });
          this.form[this.formControls.ORGANIZATION]?.enable();
          const organization = orgResponse.data[0];

          this.selectedOrganization =
            this.editMode || this.viewMode
              ? this.data?.organization
              : organization;

          if (orgResponse.data?.length === 1) {
            this.form[this.formControls.ORGANIZATION]?.setValue(organization);
            this.form[this.formControls.ORGANIZATION]?.disable();
            this.getVerticals();
          } else if (
            this.parentComponent === MODULE_NAME.TEMPLATE &&
            this.viewMode
          ) {
            this.getVerticals();
          } else if (
            (this.parentComponent === MODULE_NAME.FORMS ||
              this.parentComponent === MODULE_NAME.CAMPAIGN) &&
            this.editMode
          ) {
            this.getVerticals();
          }

          if (
            this.parentComponent === MODULE_NAME.TEMPLATE &&
            (((this.data?.status?.toUpperCase() === TemplateStatus.PUBLISHED ||
              this.data?.status?.toUpperCase() === TemplateStatus.REJECTED) &&
              this.editMode) ||
              this.viewMode)
          ) {
            this.form[this.formControls.ORGANIZATION]?.disable();
          }
        } else if (!orgResponse.error && !orgResponse.data?.length) {
          this.form[this.formControls.ORGANIZATION]?.enable();
          this.form[this.formControls.ORGANIZATION]?.setValue('');
        } else if (orgResponse?.errors?.length) {
          this.commonService.showErrorMessage(
            orgResponse?.errors[0].errorMessage
          );
        }
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.commonService.showError(errorResponse);
      },
    });
  }

  onOrganizationSelect(event: { value: string }): void {
    this.selectedOrganization = event.value;
    this.setResetValidators(
      [
        this.formControls.VERTICAL,
        this.formControls.DIVISION,
        this.formControls.DEPARTMENT,
      ],
      true,
      false
    );
    this.refreshOrDisableControls(
      [
        this.formControls.VERTICAL,
        this.formControls.DIVISION,
        this.formControls.DEPARTMENT,
      ],
      true
    );
    this.getVerticals();
  }

  getVerticals(): void {
    this.orgDetailsService
      .getVerticalsList(this.selectedOrganization)
      .subscribe({
        next: (verticalsResponse: IVerticalsResponse) => {
          if (!verticalsResponse.error && verticalsResponse.data?.length) {
            this.orgDetails.verticals = verticalsResponse.data.map((vrtcl) => {
              return {
                key: vrtcl,
                value: vrtcl,
              };
            });

            this.form[this.formControls.VERTICAL]?.enable();
            const vertical =
              this.form[this.formControls.VERTICAL]?.value ||
              verticalsResponse.data[0];

            this.selectedVertical =
              this.editMode || this.viewMode ? this.data?.vertical : vertical;

            if (verticalsResponse.data?.length === 1) {
              this.form[this.formControls.VERTICAL]?.setValue(vertical);
              this.form[this.formControls.VERTICAL]?.disable();
              this.getDivisions();
            } else if (this.form[this.formControls.VERTICAL]?.value) {
              this.form[this.formControls.VERTICAL]?.setValue(vertical);
              if (this.copyMode) {
                this.form[this.formControls.VERTICAL]?.disable();
              }
              this.getDivisions();
            } else if (
              this.parentComponent === MODULE_NAME.TEMPLATE &&
              this.viewMode
            ) {
              this.getDivisions();
            } else if (
              (this.parentComponent === MODULE_NAME.FORMS ||
                this.parentComponent === MODULE_NAME.CAMPAIGN) &&
              this.editMode
            ) {
              this.getDivisions();
            }

            if (
              this.parentComponent === MODULE_NAME.TEMPLATE &&
              (((this.data?.status?.toUpperCase() ===
                TemplateStatus.PUBLISHED ||
                this.data?.status?.toUpperCase() === TemplateStatus.REJECTED) &&
                this.editMode) ||
                this.viewMode)
            ) {
              this.form[this.formControls.VERTICAL]?.disable();
            }
          } else if (verticalsResponse?.errors?.length) {
            this.commonService.showErrorMessage(
              verticalsResponse?.errors[0].errorMessage
            );
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.commonService.showError(errorResponse);
        },
      });
  }

  onVerticalSelect(event: { value: string }): void {
    this.selectedVertical = event.value;
    this.setResetValidators(
      [this.formControls.DIVISION, this.formControls.DEPARTMENT],
      true,
      false
    );
    this.refreshOrDisableControls(
      [this.formControls.DIVISION, this.formControls.DEPARTMENT],
      true
    );
    this.getDivisions(true);
  }

  getDivisions(isVerticalSelected = false): void {
    this.orgDetailsService
      .getDivisionsList(this.selectedOrganization, this.selectedVertical)
      .subscribe({
        next: (divisionsResponse: IDivisionsResponse) => {
          if (!divisionsResponse.error && divisionsResponse.data?.length) {
            this.orgDetails.divisions = divisionsResponse.data.map((dvsn) => {
              return {
                key: dvsn,
                value: dvsn,
              };
            });
            this.form[this.formControls.DIVISION]?.enable();
            const division =
              this.form[this.formControls.DIVISION]?.value ||
              divisionsResponse.data[0];

            this.selectedDivision = isVerticalSelected
              ? division
              : this.editMode || this.viewMode
              ? this.data?.division
              : division;

            if (divisionsResponse.data?.length === 1) {
              this.form[this.formControls.DIVISION]?.setValue(division);
              if (!this.copyMode) {
                this.form[this.formControls.DIVISION]?.disable();
              }
              this.getDepartments();
            } else if (this.form[this.formControls.DIVISION]?.value) {
              this.form[this.formControls.DIVISION]?.setValue(division);
              this.getDepartments();
            } else if (
              this.parentComponent === MODULE_NAME.TEMPLATE &&
              this.viewMode
            ) {
              this.getDepartments();
            } else if (
              this.parentComponent === MODULE_NAME.FORMS &&
              this.editMode
            ) {
              if (!isVerticalSelected) {
                this.getDepartments();
              }
            }

            if (
              this.parentComponent === MODULE_NAME.TEMPLATE &&
              (((this.data?.status?.toUpperCase() ===
                TemplateStatus.PUBLISHED ||
                this.data?.status?.toUpperCase() === TemplateStatus.REJECTED) &&
                this.editMode) ||
                this.viewMode ||
                this.copyMode)
            ) {
              this.form[this.formControls.DIVISION]?.disable();
            }
          } else if (divisionsResponse?.errors?.length) {
            this.commonService.showErrorMessage(
              divisionsResponse?.errors[0].errorMessage
            );
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.commonService.showError(errorResponse);
        },
      });
  }

  onDivisionSelect(event: { value: string }): void {
    this.selectedDivision = event.value;
    this.setResetValidators([this.formControls.DEPARTMENT], true, false);
    this.refreshOrDisableControls([this.formControls.DEPARTMENT]);
    this.getDepartments();
  }

  getDepartments(): void {
    this.orgDetailsService
      .getDepartmentsList(
        this.selectedOrganization,
        this.selectedVertical,
        this.selectedDivision
      )
      .subscribe({
        next: (departmentsResponse: IDepartmentsResponse) => {
          if (!departmentsResponse.error && departmentsResponse.data?.length) {
            this.orgDetails.departments = departmentsResponse.data.map(
              (dprtmnt) => {
                return {
                  key: dprtmnt,
                  value: dprtmnt,
                };
              }
            );
            this.form[this.formControls.DEPARTMENT]?.enable();
            const department =
              this.form[this.formControls.DEPARTMENT]?.value ||
              departmentsResponse.data[0];

            if (departmentsResponse.data?.length === 1) {
              this.form[this.formControls.DEPARTMENT]?.setValue(department);
              this.form[this.formControls.DEPARTMENT]?.disable();
            } else if (this.form[this.formControls.DEPARTMENT]?.value) {
              this.form[this.formControls.DEPARTMENT]?.setValue(department);
            }

            if (
              this.parentComponent === MODULE_NAME.TEMPLATE &&
              (((this.data?.status?.toUpperCase() ===
                TemplateStatus.PUBLISHED ||
                this.data?.status?.toUpperCase() === TemplateStatus.REJECTED) &&
                this.editMode) ||
                this.viewMode)
            ) {
              this.form[this.formControls.DEPARTMENT]?.disable();
            }
          } else if (departmentsResponse?.errors?.length) {
            this.commonService.showErrorMessage(
              departmentsResponse?.errors[0].errorMessage
            );
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.commonService.showError(errorResponse);
        },
      });
  }

  setResetValidators(
    controls: string[],
    resetValue = false,
    clear = false
  ): void {
    controls.forEach((control) => {
      if (resetValue) {
        this.inputForm?.get(control)?.setValue('');
      }

      if (clear) {
        this.inputForm.get(control)?.clearValidators();
      } else {
        this.inputForm.get(control)?.setValidators(Validators.required);
      }
      this.inputForm.get(control)?.updateValueAndValidity();
    });
  }

  refreshOrDisableControls(controls: string[], disable = false): void {
    controls.forEach((control) => {
      if (disable) {
        this.inputForm.get(control)?.disable();
      }
      this.inputForm.get(control)?.markAsPristine();
      this.inputForm.get(control)?.markAsUntouched();
    });
  }
}
